import React, { Fragment, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { useConvertToModule } from "./anthologyServices";
import withAuthorization from "../../auth/withAuthorization";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../organize/OrganizeProvider";
import { notBlank } from "../../util/ext";
import { MediaContext } from "../common/media-context";
import EditDescriptionColumn from "../common/EditDescriptionColumn";
import {
  firstError,
  hasError,
  validateConvertToModule,
  validateConvertToModuleField,
} from "./convertToModuleValidation";

const useStyles = makeStyles(theme => ({
  title: {
    color: theme.palette.brandPrimary,
  },

  loader: {
    position: "absolute",
  },

  field: {
    marginBottom: theme.spacing(4),
  },

  displayName: {
    marginBottom: theme.spacing(10),
  },
}));

const emptyForm = anthology => ({
  name: anthology?.name ?? "",
  displayName: anthology?.name ?? "",
  description: anthology?.description ?? "",
  descriptionText: anthology?.descriptionText ?? "",
});

const ConvertToModule = ({ anthology }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm(anthology));
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const alert = useAlert();
  const convertToModule = useConvertToModule();
  const isLoading = convertToModule.isPending;

  useEffect(() => {
    if (open) {
      setFormData(emptyForm(anthology));
      setErrors({});
    }
  }, [open, anthology]);

  const handleCancel = () => {
    if (!isLoading) setOpen(false);
  };

  const handleConfirm = async () => {
    const validation = validateConvertToModule(formData);
    if (notBlank(validation)) {
      setErrors(validation);
      return;
    }

    try {
      const module = await convertToModule.mutateAsync({
        resourceIds: { id: anthology.id },
        data: {
          name: formData.name,
          displayName: formData.displayName,
          description: formData.descriptionText,
        },
      });
      setOpen(false);
      navigate(
        `/creator/organize?view=organize,organize-grouped,module&id=${module.id}`,
      );
    } catch (error) {
      setOpen(false);
      alert.show("error", [
        error?.message ?? "Failed to convert anthology to module.",
      ]);
    }
  };

  const convertDisabled =
    isLoading ||
    notBlank(errors) ||
    !formData.name ||
    !formData.displayName ||
    !formData.descriptionText;

  return (
    <Fragment>
      <Tooltip title="Convert to Module">
        <IconButton onClick={() => setOpen(true)} size="large">
          <FontAwesomeIcon icon={faArrowRightArrowLeft} />
        </IconButton>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleCancel}
        disableEscapeKeyDown={isLoading}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Typography classes={{ root: classes.title }}>
            Convert to Module
          </Typography>
        </DialogTitle>

        <MediaContext.Provider
          value={{
            media: formData,
            setMedia: setFormData,
            errors: errors,
            setErrors: setErrors,
          }}
        >
          <DialogContent>
            <TextField
              fullWidth
              label="Title"
              className={classes.field}
              value={formData.name}
              onChange={e =>
                setFormData(prev => ({ ...prev, name: e.target.value }))
              }
              error={hasError(errors, "name")}
              helperText={firstError(errors, "name")}
              onBlur={() =>
                setErrors(
                  validateConvertToModuleField("name", formData.name, errors),
                )
              }
            />
            <TextField
              fullWidth
              label="Display Name"
              className={classes.displayName}
              value={formData.displayName}
              onChange={e =>
                setFormData(prev => ({ ...prev, displayName: e.target.value }))
              }
              error={hasError(errors, "displayName")}
              helperText={firstError(errors, "displayName")}
              onBlur={() =>
                setErrors(
                  validateConvertToModuleField(
                    "displayName",
                    formData.displayName,
                    errors,
                  ),
                )
              }
            />
            <EditDescriptionColumn
              error={hasError(errors, "descriptionText")}
              helperText={firstError(errors, "descriptionText")}
              onBlur={() =>
                setErrors(
                  validateConvertToModuleField(
                    "descriptionText",
                    formData.descriptionText,
                    errors,
                  ),
                )
              }
            />
          </DialogContent>
        </MediaContext.Provider>

        <DialogActions>
          <Button
            onClick={handleCancel}
            disabled={isLoading}
            style={{ fontWeight: 400 }}
            autoFocus
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={convertDisabled}
            color="primary"
            variant="contained"
            style={{ position: "relative" }}
          >
            {isLoading && (
              <CircularProgress size={20} classes={{ root: classes.loader }} />
            )}
            Convert
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default withAuthorization(ConvertToModule, false);
